// ============================================================
// FILE: PublicBuyerProfile.js
// LOCATION: C:\gardenmarket\web\src\pages\gardener\PublicBuyerProfile.js
// ============================================================
// PURPOSE:
//   Read-only public profile page a gardener sees when clicking
//   a buyer's name on a job card. Shows the buyer's name, suburb,
//   member since date, and review history as a buyer.
//
// ROUTE: /buyer-profile/:id   (id = buyer's user_id)
//
// CALLS API:
//   GET /api/reviews/buyer/:id  — buyer's review history
//   GET /api/users/public/:id   — buyer's basic public info
//
// NOTE: This is intentionally minimal — buyers don't have a rich
//   public profile like gardeners. The main value is showing
//   a gardener how reliable/communicative a buyer has been
//   based on reviews left by previous gardeners.
// ============================================================

import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import client from '../../api/client';
import { colors, radius, card, cardHeader } from '../../theme/theme';

export default function PublicBuyerProfile() {
    const { id } = useParams(); // buyer user_id

    const [buyer,   setBuyer]   = useState(null);
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error,   setError]   = useState('');

    useEffect(() => {
        Promise.all([
            client.get(`/users/public/${id}`).catch(() => ({ data: { user: null } })),
            client.get(`/reviews/buyer/${id}`).catch(() => ({ data: { reviews: [] } })),
        ]).then(([u, r]) => {
            setBuyer(u.data.user);
            setReviews(r.data.reviews || []);
        }).catch(err => setError(err.message))
          .finally(() => setLoading(false));
    }, [id]);

    if (loading) return <div style={styles.loading}>Loading profile...</div>;

    return (
        <div style={styles.page}>
            <div style={styles.backRow}>
                <button onClick={() => window.history.back()} style={styles.backBtn}>← Back</button>
            </div>

            {/* Buyer summary */}
            <div style={styles.header}>
                <div style={styles.avatar}>👤</div>
                <div>
                    <h1 style={styles.name}>{buyer?.first_name} {buyer?.last_name?.[0]}.</h1>
                    {buyer?.suburb_name && <p style={styles.suburb}>📍 {buyer.suburb_name}</p>}
                    {buyer?.created_at && (
                        <p style={styles.memberSince}>
                            Member since {new Date(buyer.created_at).toLocaleDateString('en-AU', { month:'long', year:'numeric' })}
                        </p>
                    )}
                </div>
            </div>

            {error && <div style={styles.error}>{error}</div>}

            {/* Reviews from gardeners */}
            <div style={card}>
                <h3 style={cardHeader}>Reviews from Gardeners ({reviews.length})</h3>
                {reviews.length === 0 ? (
                    <p style={styles.noReviews}>No reviews yet — this buyer is new to the platform.</p>
                ) : (
                    reviews.map(r => (
                        <div key={r.review_id} style={styles.review}>
                            <div style={styles.reviewHeader}>
                                <span style={styles.stars}>{'★'.repeat(r.star_rating)}{'☆'.repeat(5 - r.star_rating)}</span>
                                <span style={styles.reviewDate}>{new Date(r.created_at).toLocaleDateString()}</span>
                            </div>
                            {r.comment && <p style={styles.comment}>"{r.comment}"</p>}
                            <p style={styles.reviewMeta}>{r.reviewer_display_name} · {r.job_title}</p>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}

const styles = {
    page:        { maxWidth:'600px', margin:'0 auto', padding:'32px 24px' },
    loading:     { textAlign:'center', padding:'60px', color:colors.textMuted },
    backRow:     { marginBottom:'16px' },
    backBtn:     { background:'none', border:'none', color:colors.brand, cursor:'pointer', fontSize:'0.9rem', padding:0 },
    header:      { display:'flex', gap:'20px', alignItems:'center', background:'white', borderRadius:radius.lg, padding:'24px', boxShadow:'0 2px 8px rgba(0,0,0,0.07)', marginBottom:'20px' },
    avatar:      { fontSize:'3rem', width:'64px', height:'64px', display:'flex', alignItems:'center', justifyContent:'center', backgroundColor:colors.brandLight, borderRadius:'50%' },
    name:        { fontSize:'1.5rem', fontWeight:700, color:colors.text, margin:'0 0 4px' },
    suburb:      { color:colors.textMuted, fontSize:'0.9rem', margin:'0 0 2px' },
    memberSince: { color:colors.textFaint, fontSize:'0.82rem', margin:0 },
    error:       { backgroundColor:'#fdecea', border:'1px solid #f5c2bb', borderRadius:radius.sm, padding:'10px', color:'#c0392b', marginBottom:'14px' },
    noReviews:   { color:colors.textMuted, textAlign:'center', padding:'20px 0', fontSize:'0.9rem' },
    review:      { borderBottom:`1px solid ${colors.border}`, padding:'14px 0' },
    reviewHeader:{ display:'flex', justifyContent:'space-between', marginBottom:'6px' },
    stars:       { color:'#FFC107', fontSize:'1.1rem' },
    reviewDate:  { color:colors.textFaint, fontSize:'0.82rem' },
    comment:     { color:colors.text, fontSize:'0.92rem', fontStyle:'italic', lineHeight:'1.5', marginBottom:'6px' },
    reviewMeta:  { color:colors.textFaint, fontSize:'0.8rem' },
};
